import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiPut } from "../api/client";
import { checkNewTag, collectTags, MAX_TAG_LENGTH, tagErrorLabel, tagKey } from "../lib/nodeTags";
import { useAppData } from "../state/AppData";
import type { MeshNode } from "../api/types";

function tagPath(protocol: string, nativeId: string, tag: string): string {
  return `/api/nodes/${encodeURIComponent(protocol)}/${encodeURIComponent(nativeId)}/tags/${encodeURIComponent(tag)}`;
}

// Read-only chips (the map panel).
export function TagChips({ tags }: { tags: readonly string[] | undefined }) {
  if (!tags || tags.length === 0) return <>--</>;
  return (
    <span className="tag-chips">
      {tags.map((t) => (
        <span key={t} className="tag-chip">
          {t}
        </span>
      ))}
    </span>
  );
}

// The Tags control on the node page: chips with a remove button, and a field that adds on Enter.
// Anyone signed in may edit (canEdit); the saved tag list replaces this node's tags in the
// shared node cache so the Nodes tab, the filter and the map update at once.
export function NodeTagsControl({
  protocol,
  nativeId,
  tags,
  canEdit,
}: {
  protocol: string;
  nativeId: string;
  tags: readonly string[];
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const { nodes } = useAppData();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const suggestions = collectTags(nodes.values()).filter((s) => !tags.some((t) => tagKey(t) === s.key));

  const publish = (next: string[]) => {
    queryClient.setQueryData<MeshNode[]>(["global", "nodes"], (prev) =>
      prev ? prev.map((n) => (String(n.id) === nativeId ? { ...n, tags: next } : n)) : prev,
    );
  };

  const add = () => {
    const checked = checkNewTag(text);
    if (checked.tag === null) {
      setError(checked.error);
      return;
    }
    setBusy(true);
    setError(null);
    apiPut<{ added: boolean; tags: string[] }>(tagPath(protocol, nativeId, checked.tag))
      .then((r) => {
        publish(r.tags);
        if (r.added) setText("");
        else setError("Already Tagged");
      })
      .catch((e: unknown) => setError(tagErrorLabel(e instanceof Error ? e.message : "")))
      .finally(() => setBusy(false));
  };

  const remove = (tag: string) => {
    setBusy(true);
    setError(null);
    apiDelete<{ tags: string[] }>(tagPath(protocol, nativeId, tag))
      .then((r) => publish(r?.tags ?? tags.filter((t) => t !== tag)))
      .catch((e: unknown) => setError(tagErrorLabel(e instanceof Error ? e.message : "")))
      .finally(() => setBusy(false));
  };

  return (
    <div className="node-tags-control">
      <span className="node-tags-label">Tags</span>
      <span className="tag-chips">
        {tags.map((t) => (
          <span key={t} className="tag-chip">
            {t}
            {canEdit && (
              <button type="button" className="tag-chip-remove" aria-label={`Remove ${t}`} onClick={() => remove(t)} disabled={busy}>
                ×
              </button>
            )}
          </span>
        ))}
        {tags.length === 0 && !canEdit && <span className="node-tags-none">--</span>}
        {canEdit && (
          <span className="tag-add">
            <input
              type="text"
              list="node-tag-suggestions"
              value={text}
              maxLength={MAX_TAG_LENGTH + 8}
              placeholder="Type A Tag"
              aria-label="Add Tag"
              title={`Up To ${MAX_TAG_LENGTH} Characters`}
              disabled={busy}
              onChange={(e) => {
                setText(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
            />
            <button type="button" className="tag-add-btn" onClick={add} disabled={busy || text.trim() === ""}>
              Add
            </button>
            <datalist id="node-tag-suggestions">
              {suggestions.map((s) => (
                <option key={s.key} value={s.tag} />
              ))}
            </datalist>
          </span>
        )}
      </span>
      {error && <span className="node-detail-note">{error}</span>}
    </div>
  );
}
